import type { TFunction } from "i18next";
import moment, { type Moment } from "moment";
import {
  ContractDurationType,
  ContractNature,
  type ContractDurationDto,
  type CpvCodeDto,
  ProcedureType,
} from "../../../../autogen/bff-api";
import { displayDate, type SupportedLocale } from "../../../../common/formatting/displayDate";

export const getBuyerInitials = (buyerName: string) =>
  buyerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

export type TimeLeft =
  | { unit: "passed" }
  | { unit: "minutes"; minutes: number }
  | { unit: "hoursAndMinutes"; hours: number; minutes: number }
  | { unit: "days"; days: number };

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

// Single source for "how long until the deadline" across every portal surface — the countdown badge,
// the deadline card and the process overview all resolve it here, so they can never disagree. Minute
// resolution below a day: truncating to whole hours reads "Deadline passed" while bids can still be
// delivered.
export const timeLeftUntil = (deadline: string, now?: Moment): TimeLeft => {
  const minutesLeft = moment(deadline).diff(now ?? moment(), "minutes");
  if (minutesLeft <= 0) return { unit: "passed" };
  if (minutesLeft < MINUTES_PER_HOUR) return { unit: "minutes", minutes: minutesLeft };
  if (minutesLeft < MINUTES_PER_DAY) {
    return {
      unit: "hoursAndMinutes",
      hours: Math.floor(minutesLeft / MINUTES_PER_HOUR),
      minutes: minutesLeft % MINUTES_PER_HOUR,
    };
  }
  return { unit: "days", days: Math.ceil(minutesLeft / MINUTES_PER_DAY) };
};

export const formatTimeLeft = (timeLeft: TimeLeft, t: TFunction): string => {
  switch (timeLeft.unit) {
    case "passed":
      return t("Deadline passed");
    case "minutes":
      return t("{{minutes}} min left", { minutes: timeLeft.minutes });
    case "hoursAndMinutes":
      return t("{{hours}} h {{minutes}} min left", { hours: timeLeft.hours, minutes: timeLeft.minutes });
    case "days":
      return `${timeLeft.days} ${t("days_left", { count: timeLeft.days })}`;
  }
};

const timeLeftColor = (timeLeft: TimeLeft): string => {
  if (timeLeft.unit === "passed") return "smMuted";
  if (timeLeft.unit === "days" && timeLeft.days > 3) return "smMuted";
  return "smError";
};

export const getTimeLeftMeta = (deadline: string, t: TFunction, now?: Moment) => {
  const timeLeft = timeLeftUntil(deadline, now);
  return { timeLeft, label: formatTimeLeft(timeLeft, t), color: timeLeftColor(timeLeft) };
};

export const formatCpv = (cpv: CpvCodeDto, language: string) =>
  `${cpv.code} - ${language.startsWith("no") ? cpv.nameNb : cpv.nameEn}`;

export const getProcedureLabel = (procedureType: ProcedureType, t: TFunction) => t(procedureType);

export const getContractNatureLabel = (contractNature: ContractNature, t: TFunction) => {
  switch (contractNature) {
    case ContractNature.Supplies:
      return t("Goods");
    case ContractNature.Services:
      return t("Services");
    case ContractNature.Works:
      return t("Construction");
    case ContractNature.Combined:
      return t("Combined");
  }
};

export const formatEstimatedValue = (value: number, currency: string) =>
  `${value.toLocaleString("nb-NO")} ${currency}`;

export const formatShortDate = (value: string, locale?: SupportedLocale | null) =>
  displayDate(value, locale);

export const deriveContractEndDate = (
  duration: ContractDurationDto | null | undefined,
): string | null => {
  if (!duration) return null;
  if (duration.type === ContractDurationType.FixedDates) return duration.endDate ?? null;
  if (duration.type === ContractDurationType.PeriodBased && duration.startDate && duration.periodMonths != null) {
    return moment(duration.startDate).add(duration.periodMonths, "months").format("YYYY-MM-DD");
  }
  return null;
};

export const formatContractDuration = (
  duration: ContractDurationDto | null | undefined,
  t: TFunction,
  locale?: SupportedLocale | null,
): string => {
  if (!duration) return "-";
  if (duration.type === ContractDurationType.OpenEnded) {
    return duration.startDate
      ? t("Running agreement from {{date}}", { date: displayDate(duration.startDate, locale) })
      : t("Running agreement");
  }
  if (duration.type === ContractDurationType.FixedDates) {
    return duration.startDate && duration.endDate
      ? `${displayDate(duration.startDate, locale)} - ${displayDate(duration.endDate, locale)}`
      : "-";
  }
  if (duration.type === ContractDurationType.PeriodBased) {
    if (duration.periodMonths == null) return "-";
    const months = t("month", { count: duration.periodMonths });
    if (!duration.startDate) return `${duration.periodMonths} ${months}`;
    const derivedEndDate = deriveContractEndDate(duration);
    if (derivedEndDate == null) {
      return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months}`;
    }
    return `${displayDate(duration.startDate, locale)} + ${duration.periodMonths} ${months} (${t("ends {{date}}", {
      date: displayDate(derivedEndDate, locale),
    })})`;
  }
  return "-";
};
